<template>
  <div class="redirector-form">
    <drawer-dialog-controls
      :is-loading="isSaving"
      :is-pristine="false"
      @close="$emit('close')"
      @save="onSave"
    >
      Redirector Settings
    </drawer-dialog-controls>

    <div class="pa-4">
      <v-row>
        <v-col>
          <v-text-field
            v-model="form.name"
            label="Redirector domain"
            dense
            hide-details
            style="width: 220px"
          />
        </v-col>
      </v-row>

      <div class="d-flex align-center mt-4" style="gap: 24px">
        <v-select
          v-model="form.group_mirror_id"
          :items="groupItems"
          label="Group"
          item-text="text"
          item-value="value"
          dense
          hide-details
          style="max-width: 220px"
        />
        <div class="d-flex flex-column justify-space-between">
          <span class="caption grey--text text-uppercase">Primary mirror (default destination)</span>
          <div v-if="primaryMirrorName" class="d-flex align-center mt-1">
            <v-icon small color="orange" class="mr-1">mdi-crown</v-icon>
            <span class="body-2 orange--text font-weight-medium">
            {{ primaryMirrorName }}
          </span>
          </div>
        </div>
      </div>

      <v-card class="mt-6" outlined>
        <v-expansion-panels v-model="nsPanel" flat>
          <v-expansion-panel>
            <v-expansion-panel-header class="expansion-panel-header px-4">
              <div class="d-flex align-center">
                <span class="body-1 black--text">Redirector Domain Name Servers</span>
                <v-tooltip bottom>
                  <template #activator="{ on }">
                    <v-icon small class="ml-1" v-on="on">mdi-information</v-icon>
                  </template>
                  Name servers for this redirector domain
                </v-tooltip>
                <div class="ml-6">
                  <div class="caption text-uppercase grey--text">NS Status</div>
                  <v-chip
                    v-if="form.status"
                    small
                    outlined
                    :color="form.status === 'active' ? 'success' : 'grey'"
                    class="pa-2 text-capitalize"
                  >
                    {{ form.status }}
                  </v-chip>
                </div>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="expansion-panel-content">
              <v-divider />
              <div class="px-4">
                <template v-if="redirectorNsList.length">
                  <div
                    v-for="(ns, index) in redirectorNsList"
                    :key="index"
                  >
                    <div class="d-flex align-center py-3">
                      <span class="body-1 font-weight-medium">NS{{ ns.settings?.priority ?? index + 1 }}:&nbsp;</span>
                      <span>{{ String(ns.name || '').toUpperCase() }}</span>
                      <v-btn
                        icon
                        x-small
                        class="ml-2"
                        @click.stop="copyToClipboard(ns.name)"
                      >
                        <v-icon small color="success">mdi-content-copy</v-icon>
                      </v-btn>
                    </div>
                    <v-divider v-if="index < redirectorNsList.length - 1" />
                  </div>
                </template>
                <div v-else class="redirector-form__empty-ns">
                  <div class="grey--text subtitle-1">
                    Loading NS from Cloudflare in progress
                  </div>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>
    </div>
  </div>
</template>

<script>
  import { cloneDeep, merge } from 'lodash'
  import { DrawerDialogControls } from '@/components/core/DrawerDialog'

  const SKELETON_REDIRECTOR = {
    entity_id: null,
    redirector_id: null,
    status: 'new',
    name: '',
    group_mirror_id: null,
    rules: {},
    settings: {},
  }

  export default {
    name: 'RedirectorForm',
    components: {
      DrawerDialogControls,
    },
    props: {
      project: {
        type: Number,
        default: null,
      },
      redirector: {
        type: Object,
        default: null,
      },
      mirrors: {
        type: Array,
        default: () => [],
      },
      groups: {
        type: Array,
        default: () => [],
      },
      isSaving: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        form: cloneDeep(SKELETON_REDIRECTOR),
        initialGroupMirrorId: null,
        nsPanel: 0,
      }
    },
    watch: {
      redirector: {
        immediate: true,
        deep: true,
        handler(val) {
          this.initFromRedirector(val)
        },
      },
    },
    computed: {
      groupItems() {
        return (this.groups || []).map((g) => ({
          text: g.name,
          value: g.group_mirror_id
        }))
      },
      selectedGroup() {
        return (this.groups || []).find((g) => g.group_mirror_id === this.form.group_mirror_id) || null
      },
      primaryMirrorName() {
        const primaryId = this.selectedGroup?.primary_mirror_id
        if (!primaryId) return ''
        return (this.mirrors || []).find((m) => m.mirror_id === primaryId)?.name || ''
      },
      redirectorNsList() {
        return (this.form?.settings?.ns?.items || []).filter((ns) => ns?.enabled !== false)
      },
    },
    methods: {
      getNewSkeleton(redirector) {
        const base = cloneDeep(SKELETON_REDIRECTOR)
        if (!redirector) {
          return {
            ...base,
            entity_id: this.project,
          }
        }
        return merge(base, cloneDeep(redirector), {
          entity_id: redirector.entity_id ?? this.project,
          group_mirror_id: redirector.group_mirror_id ?? base.group_mirror_id,
        })
      },
      initFromRedirector(redirector) {
        this.form = this.getNewSkeleton(redirector)
        this.initialGroupMirrorId = redirector?.group_mirror_id ?? null
      },
      copyToClipboard(text) {
        if (!text) return
        navigator.clipboard.writeText(text).then(() => {
          this.$showSuccess('Copied to clipboard')
        })
      },
      onSave() {
        const payload = cloneDeep(this.form)
        payload.entity_id = this.project
        delete payload.settings?.ns
        if (!payload.redirector_id) {
          delete payload.redirector_id
        }
        if (
          payload.redirector_id != null &&
          payload.group_mirror_id !== this.initialGroupMirrorId
        ) {
          payload.rules = {}
        }
        this.$emit('save', { redirector: payload })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .redirector-form {
    .expansion {
      &-panel {
        &-header {
          min-height: 56px;
          padding: 0 16px;

          &__col {
            min-height: 40px;
          }
        }

        &-content {
          ::v-deep .v-expansion-panel-content__wrap {
            padding: 0;
          }
        }
      }
    }

    &__empty-ns {
      min-height: 128px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
</style>
